import type React from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { networkOverview } from '../api.ts';
import { InfoTip } from '../components/InfoTip.tsx';
import { formatDuration } from '../format.ts';
import { useAnnounce } from '../hooks/useAnnounce.tsx';
import { useCsvExport } from '../hooks/useCsvExport.ts';
import { useLoad } from '../hooks/useLoad.ts';
import { errorMessage } from '../messages.ts';

/**
 * Quello che il referente della rete vede (mock 4, `s-network-view`).
 *
 * È la **prima schermata che questo ruolo abbia mai avuto**: fino alla fase 6
 * la sua superficie era zero — una tabella, un valore di enumerazione, nessuna
 * rotta (`docs/DECISIONI.md`, AC15). Va quindi letta come una promessa scritta
 * prima che come un'interfaccia.
 *
 * La promessa è il vincolo 7: **dati aggregati e di servizio, mai debriefing,
 * note, segnalazioni o utenti**. Non c'è un filtro che li nasconda: non
 * esistono rotte che glieli restituiscano, e un test dei vincoli verifica che
 * quelle tabelle non compaiano nel percorso di lettura del referente.
 *
 * E lo dice anche a chi guarda, in fondo alla pagina. Non è una nota legale:
 * "alle associazioni va detto esplicitamente in onboarding cosa il finanziatore
 * vedrà" (sezione 9bis2), e la simmetria — dirlo anche al finanziatore — è ciò
 * che rende quella frase credibile. Il motivo non è solo privacy: **un
 * coordinatore che sa che il finanziatore legge i debriefing smette di
 * scriverli sinceramente**, e si perde lo strumento di supervisione che li
 * rende utili.
 *
 * È già la schermata più equilibrata del prodotto — un solo comando, i numeri
 * in cima, le note dopo i numeri — e gli interventi di oggi sono tre, mirati:
 * il titolo di pagina porta il nome della rete invece di ripetere la voce della
 * barra; la frase che spiega gli enti in difficoltà è detta **una volta** sotto
 * l'elenco invece di duecentocinquantacinque caratteri dentro ogni riga; e
 * "Cosa non è visibile" non usa più la forma delle righe che si premono per
 * quattro voci che non si premono.
 */

type Props = { headingRef?: React.RefObject<HTMLHeadingElement | null> };

/**
 * Quale delle tre frasi dire di questo ente.
 *
 * Sono tre situazioni diverse e portano a tre conversazioni diverse: chiamate
 * perse a turni coperti, fasce scoperte senza chiamate perse, o entrambe.
 *
 * Le frasi sono corte perché compaiono **per ogni ente**: con otto enti, le
 * versioni lunghe erano duemila caratteri di prosa dentro un elenco che si
 * scorre per confrontare dei numeri. Ciò che valeva per tutte — che è un
 * ordine di grandezza, che il calendario vero è dell'ente, che la risposta ce
 * l'ha il coordinamento — è detto una volta sola sotto l'elenco: era la stessa
 * frase ripetuta otto volte, e ripetuta smetteva di essere letta.
 */
function needsVolunteersKey(tenant: { unanswered: number; uncoveredWindows: number }): string {
  if (tenant.unanswered > 0 && tenant.uncoveredWindows > 0) {
    return 'network.needs_both';
  }
  return tenant.unanswered > 0 ? 'network.needs_unanswered' : 'network.needs_uncovered';
}

export function NetworkPage({ headingRef }: Props) {
  const { t, i18n } = useTranslation();
  const { say, announcer } = useAnnounce();

  const load = useCallback((signal: AbortSignal) => networkOverview({}, { signal }), []);
  const { data: overview, failed } = useLoad(load, {
    onError: (error) => say(`${t('network.load_failed')} ${errorMessage(t, error)}`, 'danger'),
  });

  const download = useCsvExport(say);
  const exportCsv = (): Promise<void> => download('/api/network/overview.csv');

  /** L'ora del giorno, nel formato della lingua di chi guarda. */
  const hourLabel = (hour: number): string => {
    try {
      return new Intl.DateTimeFormat(i18n.resolvedLanguage, {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'UTC',
      }).format(new Date(Date.UTC(2000, 0, 1, hour)));
    } catch {
      return String(hour);
    }
  };

  const peak = Math.max(1, ...(overview?.distribution.map((entry) => entry.sessions) ?? [1]));
  const struggling =
    overview?.tenants.filter((tenant) => tenant.unanswered > 0 || tenant.uncoveredWindows > 0)
      .length ?? 0;

  return (
    <main className="page" id="main" tabIndex={-1}>
      {/*
        Il titolo di pagina è il nome della rete, non la parola che la barra ha
        già detto: "La rete" era la voce di navigazione ripetuta come titolo,
        cioè una riga che non aggiungeva niente. Finché i dati non sono
        arrivati resta la parola generica — un titolo vuoto è peggio di un
        titolo generico, e questo è anche il bersaglio del collegamento di
        salto.
      */}
      <h2 ref={headingRef} tabIndex={-1}>
        {overview?.network.name ?? t('network.title')}
      </h2>
      <p className="card__sub">{t('network.description')}</p>

      {announcer}
      {!overview && !failed ? (
        <section className="card">
          <p className="visually-hidden">{t('admin.loading')}</p>
          <div aria-hidden="true">
            <span className="skeleton skeleton--wide" />
            <span className="skeleton skeleton--wide" />
            <span className="skeleton skeleton--half" />
          </div>
        </section>
      ) : null}

      {overview ? (
        <>
          <section className="card" aria-labelledby="network-totals-heading">
            <h3 id="network-totals-heading">{t('network.totals_title')}</h3>
            <dl className="facts">
              <div>
                <dt>{t('network.kpi_sessions')}</dt>
                <dd>{overview.totals.sessions}</dd>
              </div>
              <div>
                <dt>{t('network.kpi_average')}</dt>
                <dd>{formatDuration(overview.totals.averageSeconds)}</dd>
              </div>
              <div>
                <dt>{t('network.kpi_unanswered')}</dt>
                <dd>{overview.totals.unanswered}</dd>
              </div>
              <div>
                <dt>{t('network.kpi_tenants')}</dt>
                <dd>{overview.totals.activeTenants}</dd>
              </div>
              <div>
                <dt>{t('network.kpi_minutes')}</dt>
                <dd>
                  {t('network.minutes_of', {
                    used: overview.totals.minutes,
                    included: overview.network.includedMinutes,
                  })}
                </dd>
              </div>
            </dl>
            {/*
              "Senza risposta" sono persone, non un indicatore di rendimento.
              Va detto accanto al numero, non altrove — e ha la forma della
              nota, perché è la conseguenza di come si legge quel numero e non
              la spiegazione della scheda.
            */}
            <p className="note">{t('network.unanswered_note')}</p>
            <p className="note">{t('network.pool_note')}</p>
          </section>

          <section className="card" aria-labelledby="network-tenants-heading">
            <div className="section-head">
              <h3 id="network-tenants-heading">{t('network.per_tenant')}</h3>
              {/*
                L'esportazione governa questa sezione e sta accanto al titolo,
                non in fondo a otto righe: chi la cerca la cerca prima di
                scorrere. È servizio, quindi `--quiet`; il nome per esteso
                resta nel nome accessibile, invariato.
              */}
              <span className="cluster">
                <span className="count">
                  {t('network.count_tenants', { count: overview.tenants.length })}
                </span>
                <button
                  type="button"
                  className="button button--quiet"
                  aria-label={t('network.export_csv')}
                  onClick={() => void exportCsv()}
                >
                  {t('network.export_csv_short')}
                </button>
              </span>
            </div>
            <ul className="plain-list">
              {overview.tenants.map((tenant) => (
                <li
                  key={tenant.id}
                  className={
                    tenant.unanswered > 0 || tenant.uncoveredWindows > 0
                      ? 'row row--flagged'
                      : 'row'
                  }
                >
                  <div className="stack stack--tight">
                    <p className="row__title">{tenant.name}</p>
                    <p className="row__meta">
                      {t('network.tenant_line', {
                        sessions: tenant.sessions,
                        minutes: tenant.minutes,
                        unanswered: tenant.unanswered,
                      })}
                    </p>
                    {/*
                      "un ente con 12 chiamate senza risposta e 6 turni scoperti:
                      probabilmente ha bisogno di più volontari, non di più
                      minuti" (mock 4). È la riga per cui questa schermata
                      esiste: porta a una conversazione, non a una decisione
                      presa sopra la testa dell'ente.
                    */}
                    {/*
                      Tre casi, tre frasi.

                      Una frase sola che interpolava sempre entrambi i numeri
                      produceva "5 chiamate senza risposta e 0 fasce scoperte:
                      probabilmente servono più volontari" — che a turni tutti
                      coperti sposta la colpa dai turni alle persone che erano
                      in turno — e il suo speculare, un allarme dove non è
                      successo niente a nessuno (AJ18).
                    */}
                    {tenant.unanswered > 0 || tenant.uncoveredWindows > 0 ? (
                      <p className="row__meta">
                        {t(needsVolunteersKey(tenant), {
                          uncovered: tenant.uncoveredWindows,
                          unanswered: tenant.unanswered,
                        })}
                      </p>
                    ) : null}
                    {tenant.capMinutes && tenant.minutes > tenant.capMinutes ? (
                      <p className="row__meta">
                        {t('network.over_cap', { cap: tenant.capMinutes })}
                      </p>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
            {overview.tenants.length === 0 ? (
              <div className="empty">
                <p>{t('network.empty')}</p>
              </div>
            ) : null}
            {/*
              Detto una volta, sotto l'elenco: vale per tutte le righe
              evidenziate e non è un dato di nessuna di esse.
            */}
            {struggling > 0 ? <p className="note">{t('network.needs_note')}</p> : null}
          </section>

          <section className="card" aria-labelledby="network-hours-heading">
            <div className="section-head">
              <div className="section-head__title">
                <h3 id="network-hours-heading">{t('network.distribution_title')}</h3>
                <InfoTip>{t('network.distribution_hint')}</InfoTip>
              </div>
            </div>
            {/*
              Un istogramma leggibile senza vederlo: ogni riga porta l'ora e il
              numero, e la barra è decorativa. Un grafico che esiste solo come
              disegno è un grafico che metà del pubblico di questo prodotto non
              può leggere (sezione 7bis).
            */}
            {overview.distribution.length === 0 ? (
              <div className="empty">
                <p>{t('network.distribution_hidden')}</p>
              </div>
            ) : null}
            <ul className="plain-list distribution">
              {overview.distribution.map((entry) => (
                <li key={entry.hour} className="distribution__row">
                  <span className="distribution__hour">{hourLabel(entry.hour)}</span>
                  {/*
                    L'unico `style` calcolato del progetto, e resta: è la
                    larghezza che viene dai dati, e non esiste nessun modo di
                    scriverla in un foglio di stile senza inventare cento
                    classi. Tutto il resto della barra — altezza, colore,
                    raggio — sta in `.distribution__bar`.
                  */}
                  <span
                    className="distribution__bar"
                    style={{ inlineSize: `${Math.round((entry.sessions / peak) * 100)}%` }}
                    aria-hidden="true"
                  />
                  <span className="distribution__count">
                    {t('network.sessions_count', { count: entry.sessions })}
                  </span>
                </li>
              ))}
            </ul>
          </section>

          <section className="card" aria-labelledby="network-limits-heading">
            <h3 id="network-limits-heading">{t('network.not_visible_title')}</h3>
            {/*
              Quattro voci di testo, e nessuna si preme.

              Portavano `.row`, che è la forma delle righe azionabili: bordo di
              separazione, fondo al passaggio del dito, spazio a destra per i
              pulsanti che qui non ci sono mai stati. Chi guarda leggeva quattro
              righe che sembravano aprirsi e non si aprivano. Qui sono un elenco
              e basta, con il respiro di una cosa che si legge.
            */}
            <ul className="plain-list stack stack--tight">
              <li>{t('network.not_visible_debriefs')}</li>
              <li>{t('network.not_visible_reports')}</li>
              <li>{t('network.not_visible_users')}</li>
              <li>{t('network.not_visible_sessions')}</li>
            </ul>
            <p className="note">{t('network.not_visible_why')}</p>
            <p className="note">{t('network.controller_note')}</p>
          </section>
        </>
      ) : null}
    </main>
  );
}
